import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Plus, Search, SlidersHorizontal, LayoutGrid, Table2, Columns3, ChevronDown, Trash2, Pencil, Eye, X, Phone, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuCheckboxItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR, formatArea, formatDate, initials, telHref, waHref } from "@/lib/format";
import { STAGE_STYLES } from "@/lib/theme";
import { StatusBadge } from "@/components/StatusBadge";
import { EmptyState } from "@/components/EmptyState";
import { LeadFormDialog } from "@/components/LeadFormDialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const ALL_COLUMNS = [
  { key: "lead", label: "Lead", always: true },
  { key: "company", label: "Company" },
  { key: "industry", label: "Industry" },
  { key: "stage", label: "Stage" },
  { key: "value", label: "Est. Value" },
  { key: "area", label: "Area" },
  { key: "assigned", label: "Assigned To" },
  { key: "followup", label: "Next Follow-up" },
  { key: "priority", label: "Priority" },
];
const LIMIT = 25;

export default function Leads() {
  const { meta, user } = useAuth();
  const canWrite = user?.role !== "ACCOUNTS";
  const navigate = useNavigate();
  const location = useLocation();
  const [view, setView] = useState(location.state?.view || "table");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ stage: location.state?.stage || "", industry: "", priority: "", lead_source: "", assigned_to: "" });
  const [showFilters, setShowFilters] = useState(!!location.state?.stage);
  const [cols, setCols] = useState(ALL_COLUMNS.map((c) => c.key));
  const [selected, setSelected] = useState([]);
  const [sort, setSort] = useState({ by: "created_at", dir: "desc" });
  const [formOpen, setFormOpen] = useState(false);
  const [editLead, setEditLead] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [pipeline, setPipeline] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: LIMIT, sort_by: sort.by, sort_dir: sort.dir });
      if (search) params.set("search", search);
      Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
      const { data } = await api.get(`/leads?${params}`);
      setItems(data.items); setTotal(data.total);
    } catch (e) { /* */ } finally { setLoading(false); }
  }, [page, search, filters, sort]);

  const loadPipeline = useCallback(async () => {
    const { data } = await api.get("/pipeline");
    setPipeline(data.stages);
  }, []);

  useEffect(() => { if (view === "table") load(); }, [load, view]);
  useEffect(() => { if (view === "pipeline") loadPipeline(); }, [loadPipeline, view]);
  useEffect(() => {
    if (!location.state) return;
    if (location.state.view) setView(location.state.view);
    if (location.state.stage !== undefined) { setFilters((f) => ({ ...f, stage: location.state.stage || "" })); setShowFilters(!!location.state.stage); setPage(1); }
    if (location.state.refresh) { load(); loadPipeline(); }
  }, [location.state]);

  const activeFilters = Object.values(filters).filter(Boolean).length;
  const clearFilters = () => { setFilters({ stage: "", industry: "", priority: "", lead_source: "", assigned_to: "" }); setPage(1); };
  const pages = Math.ceil(total / LIMIT) || 1;

  const toggleSelect = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const allSelected = items.length > 0 && selected.length === items.length;

  const bulkDelete = async () => {
    await Promise.all(selected.map((id) => api.delete(`/leads/${id}`)));
    toast.success(`${selected.length} lead(s) deleted`);
    setSelected([]); load();
  };

  const doDelete = async () => {
    await api.delete(`/leads/${confirm.id}`);
    toast.success("Lead deleted");
    setConfirm(null); load();
  };

  const toggleSort = (by) => setSort((s) => ({ by, dir: s.by === by && s.dir === "desc" ? "asc" : "desc" }));

  const setFilter = (k, v) => { setFilters((f) => ({ ...f, [k]: v === "all" ? "" : v })); setPage(1); };

  const changeStage = async (leadId, stage) => {
    await api.patch(`/leads/${leadId}/stage`, { stage });
    toast.success(`Moved to ${stage}`);
    loadPipeline();
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Leads</h1>
          <p className="mt-0.5 text-sm text-slate-500">{total} total leads in your pipeline</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-slate-200 bg-white p-0.5">
            <button data-testid="view-table" onClick={() => setView("table")} className={`flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold transition ${view === "table" ? "bg-slate-900 text-white" : "text-slate-500"}`}><Table2 className="h-3.5 w-3.5" /> Table</button>
            <button data-testid="view-pipeline" onClick={() => setView("pipeline")} className={`flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold transition ${view === "pipeline" ? "bg-slate-900 text-white" : "text-slate-500"}`}><LayoutGrid className="h-3.5 w-3.5" /> Pipeline</button>
          </div>
          {canWrite && <Button data-testid="new-lead-button" onClick={() => { setEditLead(null); setFormOpen(true); }} className="gap-1.5 font-semibold"><Plus className="h-4 w-4" /> New Lead</Button>}
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="leads-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search by company, contact, phone, lead ID…" className="h-10 pl-9" />
        </div>
        <Button variant="outline" onClick={() => setShowFilters(!showFilters)} data-testid="leads-filter-toggle" className="h-10 gap-1.5">
          <SlidersHorizontal className="h-4 w-4" /> Filters {activeFilters > 0 && <span className="ml-0.5 rounded-full bg-[#0057A8] px-1.5 text-[10px] font-bold text-white">{activeFilters}</span>}
        </Button>
        {view === "table" && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="h-10 gap-1.5" data-testid="leads-columns"><Columns3 className="h-4 w-4" /> Columns</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {ALL_COLUMNS.filter((c) => !c.always).map((c) => (
                <DropdownMenuCheckboxItem key={c.key} checked={cols.includes(c.key)} onCheckedChange={() => setCols((p) => p.includes(c.key) ? p.filter((x) => x !== c.key) : [...p, c.key])}>{c.label}</DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {showFilters && (
        <div className="grid grid-cols-2 gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-3 lg:grid-cols-5">
          {[["stage", "Stage", meta?.stages], ["industry", "Industry", meta?.industries], ["priority", "Priority", meta?.priorities], ["lead_source", "Source", meta?.sources]].map(([key, label, opts]) => (
            <div key={key}>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</label>
              <Select value={filters[key] || "all"} onValueChange={(v) => setFilter(key, v)}>
                <SelectTrigger data-testid={`filter-${key}`} className="h-9"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent><SelectItem value="all">All</SelectItem>{(opts || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ))}
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Salesperson</label>
            <Select value={filters.assigned_to || "all"} onValueChange={(v) => setFilter("assigned_to", v)}>
              <SelectTrigger data-testid="filter-assigned" className="h-9"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All</SelectItem>{(meta?.users || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {activeFilters > 0 && <button onClick={clearFilters} className="col-span-full flex items-center gap-1 text-xs font-medium text-red-500"><X className="h-3 w-3" /> Clear all filters</button>}
        </div>
      )}

      {selected.length > 0 && canWrite && (
        <div className="flex items-center gap-3 rounded-xl border border-[#0057A8]/20 bg-blue-50 px-4 py-2.5">
          <span className="text-sm font-medium text-slate-700">{selected.length} selected</span>
          <Button size="sm" variant="outline" onClick={() => setConfirm({ bulk: true })} className="ml-auto gap-1 text-red-600"><Trash2 className="h-3.5 w-3.5" /> Delete</Button>
          <button onClick={() => setSelected([])} className="text-sm text-slate-500">Cancel</button>
        </div>
      )}

      {view === "table" ? (
        <TableView {...{ loading, items, cols, selected, allSelected, toggleSelect, setSelected, sort, toggleSort, navigate, setEditLead, setFormOpen, setConfirm, canWrite }} onNew={() => { setEditLead(null); setFormOpen(true); }} />
      ) : (
        <PipelineView pipeline={pipeline} navigate={navigate} onStageChange={changeStage} />
      )}

      {view === "table" && total > LIMIT && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-500">Page {page} of {pages} · {total} leads</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      )}

      <LeadFormDialog open={formOpen} onOpenChange={setFormOpen} lead={editLead} onSaved={() => { load(); loadPipeline(); }} />
      <ConfirmDialog open={!!confirm} onOpenChange={() => setConfirm(null)} title={confirm?.bulk ? "Delete selected leads?" : "Delete this lead?"}
        description="This action cannot be undone. All associated activity will be removed." confirmText="Delete"
        onConfirm={confirm?.bulk ? bulkDelete : doDelete} />
    </div>
  );
}

function TableView({ loading, items, cols, selected, allSelected, toggleSelect, setSelected, sort, toggleSort, navigate, setEditLead, setFormOpen, setConfirm, onNew, canWrite }) {
  if (loading) return <div className="space-y-2">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-14 rounded-lg" />)}</div>;
  if (items.length === 0) return <EmptyState testid="leads-empty" icon={LayoutGrid} title="No leads found" description={canWrite ? "Create your first lead or adjust your filters to see results." : "No leads match the current filters."} action={canWrite ? <Button onClick={onNew} className="gap-1.5"><Plus className="h-4 w-4" /> New Lead</Button> : null} />;

  const has = (k) => cols.includes(k);
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="leads-table">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/60 text-left">
              <th className="w-10 px-4 py-3"><Checkbox checked={allSelected} onCheckedChange={(c) => setSelected(c ? items.map((i) => i.id) : [])} data-testid="leads-select-all" /></th>
              <th className="cursor-pointer px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500" onClick={() => toggleSort("name")}>Lead</th>
              {has("industry") && <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Industry</th>}
              {has("stage") && <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Stage</th>}
              {has("value") && <th className="cursor-pointer px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500" onClick={() => toggleSort("estimated_value")}>Est. Value</th>}
              {has("area") && <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">Area</th>}
              {has("assigned") && <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Assigned</th>}
              {has("followup") && <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Follow-up</th>}
              {has("priority") && <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Priority</th>}
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((l) => {
              const overdueFollow = l.next_follow_up && new Date(l.next_follow_up) < new Date(new Date().toDateString());
              return (
                <tr key={l.id} data-testid={`lead-row-${l.id}`} onClick={() => navigate(`/leads/${l.id}`)} className="group cursor-pointer border-b border-slate-50 transition hover:bg-slate-50/70">
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}><Checkbox checked={selected.includes(l.id)} onCheckedChange={() => toggleSelect(l.id)} /></td>
                  <td className="px-4 py-3">
                    {l.company_id ? (
                      <button data-testid={`lead-company-link-${l.id}`} onClick={(e) => { e.stopPropagation(); navigate(`/companies/${l.company_id}`); }} className="truncate text-left font-semibold text-slate-800 hover:text-[#0057A8] hover:underline">{l.company_name}</button>
                    ) : (
                      <div className="font-semibold text-slate-800">{l.company_name}</div>
                    )}
                    <div className="flex items-center gap-1.5 text-xs text-slate-400"><span className="font-mono">{l.lead_code}</span> · {l.name}</div>
                  </td>
                  {has("industry") && <td className="px-4 py-3 text-slate-600">{l.industry || "—"}</td>}
                  {has("stage") && <td className="px-4 py-3"><StatusBadge value={l.stage} dot /></td>}
                  {has("value") && <td className="px-4 py-3 text-right font-mono font-semibold text-slate-800">{formatINR(l.estimated_value)}</td>}
                  {has("area") && <td className="px-4 py-3 text-right font-mono text-xs text-slate-500">{l.estimated_area ? formatArea(l.estimated_area) : "—"}</td>}
                  {has("assigned") && <td className="px-4 py-3"><div className="flex items-center gap-2"><span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-[10px] font-bold text-slate-600">{initials(l.assigned_to_name)}</span><span className="text-xs text-slate-600">{(l.assigned_to_name || "").split(" ")[0] || "—"}</span></div></td>}
                  {has("followup") && <td className="px-4 py-3"><span className={`text-xs ${overdueFollow ? "font-semibold text-red-600" : "text-slate-500"}`}>{formatDate(l.next_follow_up)}</span></td>}
                  {has("priority") && <td className="px-4 py-3"><StatusBadge value={l.priority} type="priority" /></td>}
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1 opacity-0 transition group-hover:opacity-100">
                      <button title="View" onClick={() => navigate(`/leads/${l.id}`)} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Eye className="h-4 w-4" /></button>
                      {canWrite && <button title="Edit" onClick={() => { setEditLead(l); setFormOpen(true); }} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Pencil className="h-4 w-4" /></button>}
                      {canWrite && <button title="Delete" onClick={() => setConfirm(l)} className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PipelineView({ pipeline, navigate, onStageChange }) {
  if (!pipeline) return <div className="flex gap-4 overflow-x-auto pb-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-96 w-72 shrink-0 rounded-xl" />)}</div>;
  return (
    <div className="flex gap-4 overflow-x-auto pb-4" data-testid="pipeline-board">
      {pipeline.map((col) => {
        const style = STAGE_STYLES[col.stage] || {};
        return (
          <div key={col.stage} data-testid={`pipeline-stage-${col.stage.toLowerCase().replace(/ /g, "-")}`} className="flex w-72 shrink-0 flex-col rounded-xl border border-slate-200 bg-slate-50/50">
            <div className="border-b border-slate-200 px-3.5 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: style.dot }} />
                  <span className="text-sm font-semibold text-slate-700">{col.stage}</span>
                  <span className="rounded-full bg-white px-1.5 py-0.5 text-[10px] font-bold text-slate-500 border border-slate-200">{col.count}</span>
                </div>
                <div className="flex flex-col items-end leading-tight">
                  <span data-testid={`pipeline-${col.stage.toLowerCase().replace(/ /g, "-")}-pre-gst`} className="font-mono text-xs font-semibold text-slate-700">{formatINR(col.value)}</span>
                  <span data-testid={`pipeline-${col.stage.toLowerCase().replace(/ /g, "-")}-post-gst`} className="font-mono text-[10px] font-medium text-slate-400">incl. GST {formatINR(col.value_with_gst ?? col.value)}</span>
                </div>
              </div>
            </div>
            <div className="flex-1 space-y-2 overflow-y-auto p-2.5" style={{ maxHeight: "calc(100vh - 320px)" }}>
              {col.leads.length === 0 ? <div className="py-8 text-center text-xs text-slate-300">No leads</div> :
                col.leads.map((l) => (
                  <div key={l.id} onClick={() => navigate(`/leads/${l.id}`)} className="cursor-pointer rounded-lg border border-slate-200 bg-white p-3 transition hover:shadow-md" style={{ borderLeft: `3px solid ${style.dot}` }}>
                    <div className="flex items-start justify-between gap-2">
                      {l.company_id ? (
                        <button data-testid={`pipeline-company-link-${l.id}`} onClick={(e) => { e.stopPropagation(); navigate(`/companies/${l.company_id}`); }} className="truncate text-left text-sm font-semibold text-slate-800 hover:text-[#0057A8] hover:underline">{l.company_name}</button>
                      ) : (
                        <div className="text-sm font-semibold text-slate-800">{l.company_name}</div>
                      )}
                      <StatusBadge value={l.priority} type="priority" className="scale-90" />
                    </div>
                    <div className="mt-0.5 text-xs text-slate-500">{l.name}</div>
                    <div className="mt-2.5 flex items-center justify-between">
                      <div className="flex flex-col leading-tight">
                        <span className="font-mono text-sm font-bold text-slate-900">{formatINR(l.estimated_value)}</span>
                        <span className="font-mono text-[10px] text-slate-400">incl. GST {formatINR((l.estimated_value || 0) * (1 + (((l.cgst_pct ?? 9) + (l.sgst_pct ?? 9)) / 100)))}</span>
                      </div>
                      {l.estimated_area > 0 && <span className="font-mono text-[10px] text-slate-400">{formatArea(l.estimated_area)}</span>}
                    </div>
                    <div className="mt-2.5 flex items-center justify-between border-t border-slate-50 pt-2.5">
                      <div className="flex items-center gap-1.5">
                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-100 text-[9px] font-bold text-slate-600">{initials(l.assigned_to_name)}</span>
                        <span className="text-[10px] text-slate-500">{(l.assigned_to_name || "").split(" ")[0]}</span>
                      </div>
                      {l.lead_source && <span className="rounded bg-slate-50 px-1.5 py-0.5 text-[9px] font-medium text-slate-500">{l.lead_source}</span>}
                    </div>
                    {l.next_follow_up && <div className="mt-1.5 text-[10px] text-slate-400">Follow-up: {formatDate(l.next_follow_up)}</div>}
                  </div>
                ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
